/**
 * Theme state.
 *
 * A rune-based module singleton rather than a store: the theme is a single value
 * with a single owner, and a plain object with `$state` gives the same reactivity
 * with no subscription lifecycle to manage.
 *
 * The state holds the visitor's *preference* (`dark` | `light` | `system`). The
 * scheme actually applied is derived, because `system` has to be resolved against
 * the OS setting and stay live when the OS setting changes.
 */
import { config } from '$lib/config';
import type { ColorScheme } from '$lib/config/schema';
import {
	applyTheme,
	resolveScheme,
	systemPrefersDark,
	type EffectiveScheme
} from '$lib/theme/applyTheme';
import { readStored, writeStored } from '$lib/utils/storage';

const STORAGE_KEY = 'meadow-map:color-scheme';

const themeConfig = config.site.theme;

function isColorScheme(value: unknown): value is ColorScheme {
	return value === 'dark' || value === 'light' || value === 'system';
}

function initialPreference(): ColorScheme {
	const stored = readStored(STORAGE_KEY);
	return isColorScheme(stored) ? stored : themeConfig.colorScheme;
}

function createThemeState() {
	/** The visitor's choice, persisted across visits. */
	let preference = $state<ColorScheme>(initialPreference());

	/** Mirrors the OS preference so `system` reacts to it without a reload. */
	let prefersDark = $state(true);

	/** The scheme in effect right now. */
	const scheme = $derived<EffectiveScheme>(resolveScheme(preference, prefersDark));

	function set(next: ColorScheme): void {
		if (!themeConfig.switchable) return;
		preference = next;
		writeStored(STORAGE_KEY, next);
	}

	/** Writes the active palette to the document. Safe to call on every change. */
	function apply(): void {
		if (typeof document === 'undefined') return;
		applyTheme(themeConfig, scheme, document.documentElement);
	}

	return {
		get preference(): ColorScheme {
			return preference;
		},

		get scheme(): EffectiveScheme {
			return scheme;
		},

		/** False when the deployment does not offer a theme toggle. */
		get switchable(): boolean {
			return themeConfig.switchable;
		},

		set,

		/** Flips between dark and light, resolving `system` to its opposite. */
		toggle(): void {
			set(scheme === 'dark' ? 'light' : 'dark');
		},

		/**
		 * Starts following the OS preference.
		 *
		 * Returns a teardown function. Called once from the root layout, so the
		 * listener has an owner and cannot leak across navigations.
		 */
		watchSystem(): () => void {
			if (typeof window === 'undefined' || !window.matchMedia) return () => {};

			prefersDark = systemPrefersDark();
			const query = window.matchMedia('(prefers-color-scheme: dark)');
			const onChange = (event: MediaQueryListEvent) => {
				prefersDark = event.matches;
			};

			query.addEventListener('change', onChange);
			return () => query.removeEventListener('change', onChange);
		},

		apply
	};
}

export const themeState = createThemeState();
